import { useState, useEffect, useMemo } from "react";

import { STRINGS } from "../../../utils/string";
import AddressField from "../../Fields/AddressField";

import CompanyFields from "./components/CompanyFields";
import ContactInfoFields from "./components/ContactInfoFields";
import ModalActions from "./components/ModalActions";
import ModalHeader from "./components/ModalHeader";
import NameTitleFields from "./components/NameTitleFields";

export default function AddEditModal({ mode, additionalDetails = {} }) {
  const { relationType, data, handleAdd, handleEdit, handleCancel } = additionalDetails;

  const modeLabel = useMemo(
    () =>
      mode === STRINGS.MODAL_MODE.ADD ? "Add" : mode === STRINGS.MODAL_MODE.EDIT ? "Edit" : "",
    [mode]
  );

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    companyName: "",
    email: "",
    phone: "",
    address: "",
    relationType: relationType?.key || "",
  });
  const [originalData, setOriginalData] = useState(null);

  const { name, title, companyName, email, phone, address } = formData;

  useEffect(() => {
    if (mode === STRINGS.MODAL_MODE.EDIT && data) {
      const initData = {
        id: data.id,
        name: data.name || "",
        title: data.title || "",
        companyName: data.companyName || "",
        email: data.email || "",
        phone: data.phone || "",
        address: data.address || "",
      };
      setFormData(initData);
      setOriginalData(initData);
    }
  }, [mode, data]);

  const handleChange = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const getChangedFields = () =>
    mode === STRINGS.MODAL_MODE.EDIT && originalData
      ? Object.fromEntries(
          Object.entries(formData).filter(([k, v]) => k === "id" || v !== originalData[k])
        )
      : formData;

  const hasChanges = useMemo(() => {
    if (mode === STRINGS.MODAL_MODE.ADD) {
      return (
        name.trim() !== "" ||
        title.trim() !== "" ||
        companyName.trim() !== "" ||
        email.trim() !== "" ||
        phone.trim() !== "" ||
        (address && address !== "")
      );
    }
    if (mode === STRINGS.MODAL_MODE.EDIT && originalData) {
      return Object.keys(formData).some((key) => formData[key] !== originalData[key]);
    }
    return false;
  }, [mode, formData, originalData, name, title, companyName, email, phone, address]);

  const handleSubmit = () => {
    const payload = { ...getChangedFields() };
    if (address?.id) {
      payload.address = address.id;
    }
    mode === STRINGS.MODAL_MODE.ADD ? handleAdd?.(payload) : handleEdit?.(payload);
  };

  return (
    <div className="flex w-full flex-col rounded-[12px] px-[36px] py-[38px]">
      <ModalHeader
        modeLabel={modeLabel}
        relationTypeValue={relationType?.value || data?.relationType?.value}
      />
      <div className="flex flex-col py-4">
        <NameTitleFields name={name} title={title} onChange={handleChange} />
        <CompanyFields companyName={companyName} onChange={handleChange} />
        <ContactInfoFields email={email} phone={phone} onChange={handleChange} />
        <AddressField
          value={address}
          onChange={(addr) => setFormData((p) => ({ ...p, address: addr }))}
          className="pb-4"
          mode={mode}
        />
        <ModalActions
          onCancel={handleCancel}
          onSave={handleSubmit}
          disabled={!hasChanges}
          modeLabel={modeLabel}
        />
      </div>
    </div>
  );
}
